import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    DefaultDataService,
    DefaultDataServiceConfig,
    HttpUrlGenerator
} from '@ngrx/data';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as fromShared from '../../../shared';
import { IGenericResponse, ResponseStatus } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { ContractSummary } from './contract-summary.model';

export const entityName = 'ContractBudgetSummary';

@Injectable()
export class ContractSummaryDataService extends DefaultDataService<ContractSummary> {
    private uri: string;
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        private log: LoggingService,
        private config: DefaultDataServiceConfig,
        private store: Store
    ) {
        super(entityName, http, httpUrlGenerator, config);
        log.verbose({ type: 'ContractSummaryDataService', msg: '::', config });
        // entityResource gives the singular version of the entity name (EmployeePayOvr in this case)
        this.entitiesUrl = httpUrlGenerator.entityResource(
            entityName,
            config.root as string,
            false
        );
    }
    getAll(): Observable<ContractSummary[]> {
        this.log.verbose({ type: 'ContractSummaryDataService:getAll' });
        return super.getAll().pipe(
            tap(contracts =>
                this.log.verbose({
                    type: 'ContractSummaryDataService:getAll',
                    contracts
                })
            ),
            map(Contracts => Contracts.map(p => this.mapContractSummary(p))),
            catchError((e, c) =>
                fromShared.Errors.handleDataServiceError(this.store, e, c)
            )
        );
    }
    update(update: Update<ContractSummary>): Observable<ContractSummary> {
        this.log.verbose({ type: 'ContractSummaryDataService:update', update });
        // return super.update(update);
        return this.save(update);
    }
    upsert(entity: ContractSummary): Observable<ContractSummary> {
        this.log.verbose({ type: 'ContractSummaryDataService:upsert', entity });
        return this.save({ id: entity.id as any, changes: entity });
        // return super.upsert(entity);
    }
    private save(update: Update<ContractSummary>): Observable<ContractSummary> {
        this.log.verbose({ type: 'ContractSummaryDataService:save', update });
        const item = {
            ...update.changes
        } as any;
        delete item.authorization;
        delete item.original;
        return this.http
            .put<IGenericResponse<ContractSummary>>(this.entitiesUrl, {
                [`${entityName}List`]: [item]
            })
            .pipe(
                map(resp => {
                    this.log.verbose({
                        type: 'ContractSummaryDataService:save:resp',
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.map(x => ({
                                        ...x,
                                        Status: {
                                            ...x.Status,
                                            MsgEntityID: update.changes.original
                                                ? update.changes.original.id
                                                : update.changes.id || update.id
                                        }
                                    }))
                                })
                            )
                        );
                        throwError(
                            new Error(
                                resp.ErrorMessages.reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                )
                            )
                        );
                        return {
                            // ...this.mapContractSummary(update.changes as ContractSummary),
                            ...update.changes.original,
                            Status: resp.Items[0].Status
                        } as ContractSummary;
                    }
                    return {
                        ...this.mapContractSummary(resp.Items[0]),
                        Status: {
                            StatusCode: ResponseStatus.Success,
                            Message: 'Saved'
                        }
                    } as ContractSummary;
                    // resp.Items[0].id
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }

    private mapContractSummary(p: ContractSummary): ContractSummary {
        return {
            ...p,
            id: p.Contractlistid,
            BudgetEndDate: moment(p.BudgetEndDate).isValid()
                ? moment(p.BudgetEndDate).toDate()
                : null,
            Totalamountvariance: p.Totalamountbudget - p.Totalamountspent,
            Totalhoursvariance: p.Totalhoursbudget - p.Totalhoursspent
        };
    }
}
